import cloneDeep from 'lodash-es/cloneDeep';
import cloneDeepWith from 'lodash-es/cloneDeepWith';
import compact from 'lodash-es/compact';
import countBy from 'lodash-es/countBy';
import first from 'lodash-es/first';
import flatMap from 'lodash-es/flatMap';
import get from 'lodash-es/get';
import isFunction from 'lodash-es/isFunction';
import keyBy from 'lodash-es/keyBy';
import noop from 'lodash-es/noop';
import omit from 'lodash-es/omit';
import range from 'lodash-es/range';
import reverse from 'lodash-es/reverse';
import set from 'lodash-es/set';
import startCase from 'lodash-es/startCase';
import snakeCase from 'lodash-es/snakeCase';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';

import merge from 'lodash-es/merge';
import uuid from 'uuid';

import { getObjectType, isObjectOfType, applyOnObjectTree } from '../objects';

import variables from '../variables';
import { getObjectsDiff } from '../canvas';
import utils from '../utils';
import { migrateSlideContent } from '../migrations';

export const TEMPLATE_TYPE_BLANK = 'blank';
export const TEMPLATE_TYPE_CUSTOM = 'custom';

const COPIABLE_KEY_PATHS_BY_TYPE = {
  title: ['text', 'contents'],
  paragraph: ['text', 'contents'],
  image: [
    'ludus.publicId',
    'ludus.format',
    'ludus.version',
    'ludus.source',
    'ludus.url',
    'ludus.originalUrl',
    'ludus.originalWidth',
    'ludus.originalHeight',
    'ludus.limitedWidth',
    'ludus.limitedHeight',
    'ludus.ludusImageVersion',
    'ludus.backgroundColor',
    'ludus.textColor',
    'ludus.uploadState',
    'imageCropX',
    'imageCropY',
    'cropWidth',
    'cropHeight',
    'imageScaleX',
    'imageScaleY',
  ],
};

export const TEMPLATE_DEFAULT_ID = null;
export const TEMPLATE_DEFAULT_SIZE = variables.documentSizes.default;
export const TEMPLATE_DEFAULT_CONTENT = {
  background: 'rgba(255,255,255,1)',
  objects: [],
};

export const DEFAULT_TEMPLATES = {
  [TEMPLATE_DEFAULT_ID]: {
    id: TEMPLATE_DEFAULT_ID,
    themeId: null,
    name: 'Blank',
    content: TEMPLATE_DEFAULT_CONTENT,
    version: null,
  },
};

export const DEFAULT_THEME = {
  id: null,
  default: true,
  name: 'None',
  permissions: ['use', 'edit'],
  templates: DEFAULT_TEMPLATES,
};

export const TEMPLATE_DEFAULT_NEW_NAME = 'New template';

export const THEME_DEFAULT_NEW_ID = uuid.v4();
export const THEME_DEFAULT_NEW_NAME = 'New theme';

const noRefName = 'No reference';
export const convertReferenceToName = ref => (ref ? startCase(ref) : noRefName);
export const convertNameToReference = name => {
  if (!name) {
    return null;
  }
  return name === noRefName ? null : snakeCase(name);
};

export const getReferenceGeneratorForBasename = basename => n =>
  `${basename === 'symbol' ? 'smartblock' : basename}_${n}`;

export const getReferenceGeneratorForObject = object =>
  getReferenceGeneratorForBasename(getObjectType(object));

export const getReferenceListForObject = (object, count = 10) =>
  range(1, 1 + count).map(getReferenceGeneratorForObject(object));

export const getObjectReference = object => get(object, 'ludus.reference');

export const hasObjectReference = object => !!getObjectReference(object);

export const setObjectReference = (object, reference) =>
  set(object, 'ludus.reference', reference);

export const getObjectReferenceId = object => get(object, 'ludus.referenceId');

export const hasObjectReferenceId = object => !!getObjectReferenceId(object);

export const setObjectReferenceId = (object, referenceId) =>
  set(object, 'ludus.referenceId', referenceId);

export const setObjectReferenceIfNeeded = (object, reference) => {
  if (!hasObjectReference(object)) {
    setObjectReference(object, reference);
  }
  if (!hasObjectReferenceId(object)) {
    setObjectReferenceId(object, uuid.v4());
  }
};

export const removeOverridenFlag = object => omit(object, 'ludus.overriden');

export const getAllObjectsReferences = objects =>
  objects.map(getObjectReference);

export const getNextAvailableReferenceForObjectInCollection = (
  object,
  objects,
) => {
  const referenceList = getReferenceListForObject(object, 99);
  const existingReferences = getAllObjectsReferences(Object.values(objects));
  return (
    referenceList.find(reference => !existingReferences.includes(reference)) ||
    null
  );
};

export const isTemplateObject = object =>
  getObjectReference(object) !== undefined;

export const removeObjectReference = object => {
  setObjectReference(object, undefined);
  setObjectReferenceId(object, undefined);
};

export const removeObjectsReferencesFromObjectTree = object =>
  applyOnObjectTree(object, removeObjectReference);

export const fixObjectReference = objects => object => {
  const existingReferences = getAllObjectsReferences(Object.values(objects));
  const reference = getObjectReference(object);
  if (reference === null || existingReferences.includes(reference)) {
    removeObjectReference(object);
  }
};

export const fixObjectsReferencesFromObjectTree = objects => object =>
  applyOnObjectTree(object, fixObjectReference(objects));

export const getObjectByReferenceId = (objects, referenceId) =>
  objects.find(object => get(object, 'ludus.referenceId') === referenceId) ||
  null;
export const getObjectByReference = (objects, reference) =>
  objects.find(object => get(object, 'ludus.reference') === reference) || null;

export const getTemplateCachePath = ({ id, version, width, height }) =>
  `[${id}][${version}][${width}x${height}]`;

export const getThemeById = (themes, themeId) => themes[themeId];

export const getThemeByTemplateId = (themes, templateId) =>
  Object.values(themes).find(theme =>
    Object.values(theme.templates || {}).some(
      template => template.id === templateId,
    ),
  ) || null;

export const getDefaultTheme = themes =>
  Object.values(themes).find(theme => theme.default) || null;

export const getDefaultThemeId = themes => {
  const defaultTheme = getDefaultTheme(themes);
  return defaultTheme ? defaultTheme.id : null;
};

export const getTypeByName = (types, typeName) =>
  types.find(type => type.name === typeName) || null;

export const getTemplateById = (themes, templateId) =>
  flatMap(Object.values(themes), theme =>
    Object.values(theme.templates || {}),
  ).find(template => template.id === templateId) || null;

const getAllMatchingTemplatesForThemeId = (
  themes,
  themeId,
  { type, name },
  excludedIds = [],
) => {
  const theme = getThemeById(themes, themeId);
  if (!theme) {
    return [];
  }
  return Object.values(theme.templates || {}).filter(template => {
    if (excludedIds.includes(template.id)) {
      return false;
    }
    if (template.type !== type) {
      return false;
    }

    if (type === TEMPLATE_TYPE_CUSTOM && name !== undefined && name !== '') {
      return template.name?.toLowerCase() === name?.toLowerCase();
    }
    return true;
  });
};

export const countAllMatchingTemplatesForThemeId = (
  themes,
  themeId,
  data,
  excludedIds,
) =>
  getAllMatchingTemplatesForThemeId(themes, themeId, data, excludedIds).length;

export const getMatchingTemplateForThemeId = (themes, themeId, data) =>
  first(getAllMatchingTemplatesForThemeId(themes, themeId, data)) || null;

export const getBlankTemplateByThemeId = (themes, themeId) =>
  getMatchingTemplateForThemeId(themes, themeId, { type: TEMPLATE_TYPE_BLANK });

const getNextAvailableDefaultTemplateNameInTheme = theme => {
  const templateNames = theme
    ? Object.values(theme.templates || {}).map(t => t.name)
    : [];
  if (!templateNames.includes(TEMPLATE_DEFAULT_NEW_NAME)) {
    return TEMPLATE_DEFAULT_NEW_NAME;
  }
  let n = 2;
  let newTemplateName;
  do {
    newTemplateName = `${TEMPLATE_DEFAULT_NEW_NAME} ${n}`;
    n += 1;
  } while (templateNames.includes(newTemplateName));
  return newTemplateName;
};

export const getTemplateFullName = (themes, templateId) => {
  const template = getTemplateById(themes, templateId);
  if (!template) {
    return '';
  }
  return template.name;
};

export const getDefaultNewTemplateData = themes => {
  const themeId = getDefaultThemeId(themes);
  const theme = getThemeById(themes, themeId);
  return {
    id: uuid.v4(),
    themeId,
    themeName: THEME_DEFAULT_NEW_NAME,
    name: getNextAvailableDefaultTemplateNameInTheme(theme),
    type: TEMPLATE_TYPE_BLANK,
  };
};

export const getCommonThemeForSlides = (themes, slides) => {
  let themesIds = (slides || [])
    .filter(slide => !!slide.templateId)
    .map(slide => {
      const theme = getThemeByTemplateId(themes, slide.templateId);
      return theme ? theme.id : null;
    });
  // Remove null values
  themesIds = compact(themesIds);
  // Reverse so the last slide will take precedence if frequence is equal between several themes
  themesIds = reverse(themesIds);
  const themesIdsCount = countBy(themesIds);
  const mostUsedThemeId = first(
    reverse(sortBy(uniq(themesIds), [id => themesIdsCount[id]])),
  );
  return {
    id: mostUsedThemeId,
    frequence: themesIdsCount[mostUsedThemeId] / themesIds.length,
  };
};

export const isThemeValid = (themes, theme) =>
  !!theme.name &&
  !Object.values(omit(themes, [theme.id]))
    .map(t => t.name?.toLowerCase() || '')
    .includes(theme.name.toLowerCase());

export const isTemplateValid = (
  themes,
  themeId,
  types,
  template,
  editingTheme,
) => {
  let isValid = true;
  if (template.type === TEMPLATE_TYPE_CUSTOM) {
    if (!template.name || template.name === '') {
      return isValid;
    }
    const matchingTemplates = getAllMatchingTemplatesForThemeId(
      themes,
      themeId,
      template,
    ).filter(t => t.id !== template.id);
    const typesDescriptions = types.map(type => type.description.toLowerCase());
    isValid =
      matchingTemplates.length === 0 &&
      !!template.name &&
      !typesDescriptions.includes(template.name.toLowerCase());
  }
  return isValid;
};

export const isPendingTemplateValid = (themes, templateTypes, template) => {
  if (!template) {
    return false;
  }

  if (
    template.type === 'custom' &&
    (template.name === null || template.name === '')
  ) {
    return true;
  }
  const currentTheme = getThemeById(themes, template.themeId);
  const currentThemeId = currentTheme ? currentTheme.id : THEME_DEFAULT_NEW_ID;
  return (
    (currentThemeId !== THEME_DEFAULT_NEW_ID ||
      isThemeValid(themes, {
        id: THEME_DEFAULT_NEW_ID,
        name: template.themeName,
      })) &&
    isTemplateValid(themes, currentThemeId, templateTypes, template)
  );
};

const hasThemePermission = (themes, themeId, permission) => {
  const theme = getThemeById(themes, themeId);
  return theme ? theme.permissions.includes(permission) : false;
};

export const isThemeEditable = (themes, themeId) =>
  hasThemePermission(themes, themeId, 'edit');

export const isThemeDeletable = (themes, themeId) =>
  hasThemePermission(themes, themeId, 'delete');

export const isTemplateEditable = (themes, templateId) => {
  const template = getTemplateById(themes, templateId);
  return template
    ? isThemeEditable(themes, template.themeId) &&
        template.id !== TEMPLATE_DEFAULT_ID
    : false;
};

export const isTemplateDeletable = (themes, templateId) => {
  const template = getTemplateById(themes, templateId);
  return template
    ? isThemeDeletable(themes, template.themeId) &&
        template.id !== TEMPLATE_DEFAULT_ID
    : false;
};

export const isTemplateCompatible = (themes, templateId, conditions) => {
  const template = getTemplateById(themes, templateId);
  if (template) {
    return (
      template.id === null ||
      (template.width === conditions.width &&
        template.height === conditions.height)
    );
  }
  return false;
};

export const parseGraphQLTemplateData = (template, themeId) => ({
  id: template.id,
  type: template.type,
  name: template.name,
  themeId,
  content: JSON.parse(template.content),
  version: template.hash,
  width: template.width,
  height: template.height,
});

export const parseGraphQLThemeData = theme => {
  const templates = get(theme, 'templates.edges', [])
    .map(e => e.node)
    .map(templateData => parseGraphQLTemplateData(templateData, theme.id));
  return {
    id: theme.id,
    name: theme.name,
    default: theme.default,
    public: theme.public,
    permissions: theme.permissions,
    templates: keyBy(templates, 'id'),
    width: theme.width,
    height: theme.height,
  };
};

export const parseFirebaseTheme = (themeId, theme, currentOwnerId) => {
  const ownerId = theme.owner_id || theme.ownerId;
  const permissions = ['use'];

  if (!theme.public || currentOwnerId === ownerId) {
    permissions.push('edit', 'delete');
  }

  return {
    id: themeId,
    name: theme.name,
    default: theme.default,
    public: theme.public,
    permissions: permissions,
    width: theme.width,
    height: theme.height,
    ownerId,
    order: theme.order?.split?.(',') || theme.order,
  };
};

export const parseFirebaseTemplate = (templateId, template) => {
  return {
    id: templateId,
    themeId: template.theme_id,
    name: template.name,
    type: template.type,
    width: template.width,
    height: template.height,
    content: template.content,
    version: template.hash,
  };
};

export const parseFirebaseThemesData = (themesObject, templatesObject) => {
  const templates = Object.keys(templatesObject).map(templateId => {
    const firebaseTemplate = templatesObject[templateId];

    return {
      id: templateId,
      themeId: firebaseTemplate.theme_id,
      name: firebaseTemplate.name,
      type: firebaseTemplate.type,
      width: firebaseTemplate.width,
      height: firebaseTemplate.height,
      content: firebaseTemplate.content,
      version: firebaseTemplate.hash,
    };
  });

  const parsedThemes = Object.keys(themesObject).map(themeId => {
    const theme = themesObject[themeId];

    const themeTemplates = templates.filter(
      template => template.themeId === themeId,
    );

    return {
      id: themeId,
      name: theme.name,
      default: theme.default,
      permissions: ['use'],
      templates: keyBy(themeTemplates, 'id'),
      width: theme.width,
      height: theme.height,
    };
  });

  return keyBy(parsedThemes, 'id');
};

const parseGraphQLTemplateTypeData = type => ({
  name: type.name,
  description: type.description,
});

export const parseGraphQLTemplateTypesData = types =>
  get(types, 'enumValues', []).map(parseGraphQLTemplateTypeData);

const configureTemplateObjects = (objects, enable, customize = noop) => {
  const editableProperties = ['editable', 'evented', 'selectable'];
  const newObjects = Array.isArray(objects)
    ? cloneDeep(objects)
    : cloneDeep(Object.values(objects));
  newObjects.forEach(object => {
    let enabled;
    if (isFunction(enable)) {
      enabled = enable(object);
    } else {
      enabled = enable;
    }
    editableProperties.forEach(editableProperty => {
      set(object, editableProperty, enabled);
    });
    customize(object);
  });
  return keyBy(newObjects, 'canvasId');
};

export const linkTemplateObjects = objects =>
  configureTemplateObjects(
    objects,
    object =>
      // Enable edition only for referenced objects
      hasObjectReference(object),
    object => {
      // Add missing references. All template objects should have a
      // defined reference, either a UUID or null
      setObjectReferenceIfNeeded(object, null);
    },
  );

export const unlinkTemplateObjects = objects =>
  configureTemplateObjects(objects, true, object => {
    // Remove all references
    removeObjectReference(object);
  });

export const enableTemplateObjects = objects =>
  configureTemplateObjects(objects, true);

export const convertSlideObjectsToTemplateObjects = objects => {
  if (!objects) {
    return null;
  }
  const newObjects = cloneDeepWith(Object.values(objects), object => {
    if (isObjectOfType(object, 'image')) {
      return removeOverridenFlag(object);
    }
    return object;
  });
  // Pre-add references to text blocks by default
  newObjects.forEach(object => {
    if (isObjectOfType(object, 'title', 'paragraph', 'text', 'symbol')) {
      setObjectReferenceIfNeeded(
        object,
        getNextAvailableReferenceForObjectInCollection(object, newObjects),
      );
    }
  });
  return newObjects;
};

export const applyModificationsToContent = (
  content,
  { added = [], removed = [], modified = [] },
) => {
  const { objects: slideObjects = {} } = content;

  // Forcing a deep copy
  const objects = utils.deepCopy(slideObjects);

  added.forEach(object => (objects[object.canvasId] = object));

  modified.forEach(({ originalObject, diffs }) => {
    const hasRef = get(originalObject, 'ludus.reference');
    // originalObject in template before applying it's diffs props
    const slideObject = objects[originalObject.canvasId];
    const slideDiffs = getObjectsDiff(originalObject, slideObject);
    // only merge slide Diffs if obect has a reference?

    // const copiableKeyPaths =
    //   COPIABLE_KEY_PATHS_BY_TYPE[getObjectType(templateObject)] || [];

    const mergedDiffs = hasRef
      ? merge(utils.deepCopy(diffs), slideDiffs)
      : merge(utils.deepCopy(originalObject), utils.deepCopy(diffs));

    if (
      mergedDiffs.ludus &&
      (mergedDiffs.ludus.reference || mergedDiffs.ludus.reference === null)
    ) {
      if (mergedDiffs.ludus.reference) {
        mergedDiffs.editable = true;
        mergedDiffs.evented = true;
        mergedDiffs.selectable = true;
      } else {
        mergedDiffs.editable = false;
        mergedDiffs.evented = false;
        mergedDiffs.selectable = false;
      }
    }

    // skip contents  && todo: skip images source?
    if (mergedDiffs.contents) {
      mergedDiffs.contents = slideObject.contents;
    }
    // if (mergedDiffs.contents) {
    //   mergedDiffs.contents = originalObject.contents;
    // }

    const newObject = merge(utils.deepCopy(originalObject), mergedDiffs);
    objects[originalObject.canvasId] = newObject;
  });

  removed.forEach(canvasId => delete objects[canvasId]);

  return utils.deepCopy(objects);
};

export const replaceTemplate = (template, slide, currentTemplate) => {
  const { objects: templateObjects = {}, background } = template.content;
  const { objects: currentTemplateObjects = {} } = currentTemplate.content;
  const { objects: slideObjects = {} } = slide.content;
  const _slideObjects = JSON.parse(JSON.stringify(slideObjects));
  const _templateObjects = JSON.parse(JSON.stringify(templateObjects));
  // 1. For output keep only slidesObjects not present in current template (manually added)
  Object.values(slideObjects).filter(slideObject => {
    if (currentTemplateObjects[slideObject.canvasId]) {
      delete _slideObjects[slideObject.canvasId];
    }
  });
  // 2. Find any copiableKeyPaths difference between slidesObjects and current template
  // 3. Apply these differences to template objects with same ludus.reference
  Object.values(_templateObjects).forEach(templateObject => {
    const copiableKeyPaths =
      COPIABLE_KEY_PATHS_BY_TYPE[getObjectType(templateObject)] || [];

    const templateRef = get(templateObject, 'ludus.reference');
    if (templateRef) {
      const currentObjectWithSameRef = Object.values(slideObjects).find(
        o => get(o, 'ludus.reference') === templateRef,
      );
      if (currentObjectWithSameRef) {
        copiableKeyPaths.forEach(keyPath => {
          const currentValue = get(currentObjectWithSameRef, keyPath);
          const valueInTemplate = get(
            currentTemplateObjects[currentObjectWithSameRef.canvasId],
            keyPath,
          );
          if (
            JSON.stringify(currentValue) !== JSON.stringify(valueInTemplate)
          ) {
            set(
              templateObject,
              keyPath,
              get(currentObjectWithSameRef, keyPath),
            );
          }
        });
      }
    }
  });

  // 4. return template objects (with updated ludus.ref comparaison) merge with manually added objects

  return {
    slideId: slide.id,
    templateId: template.id,
    templateVersion: template.hash || template.version,
    background,
    objects: { ..._slideObjects, ..._templateObjects },
  };
};
/**
 *
 * @param {*} template template to be applied to the slide
 * @param {*} slide slide where the template will be applied to
 * @param {*} removedObjects When editing a template, objects may have been removed.
 */
export const resetSlideFromTemplate = (
  template,
  slide,
  removedObjects = [],
  completeUpdate,
) => {
  const { objects: templateObjects = {}, background } = template.content;
  const { objects: slideObjets = {} } = slide.content;

  // Forcing a deep copy
  const objects = JSON.parse(
    JSON.stringify({ ...slideObjets, ...templateObjects }),
  );
  Object.values(slideObjets).forEach(object => {
    if (!completeUpdate) {
      const copiableKeyPaths =
        COPIABLE_KEY_PATHS_BY_TYPE[getObjectType(object)] || [];
      copiableKeyPaths.forEach(keyPath => {
        set(objects[object.canvasId], keyPath, get(object, keyPath));
      });
    }
  });

  removedObjects.forEach(canvasId => delete objects[canvasId]);
  return {
    slideId: slide.id,
    templateId: template.id,
    templateVersion: template.hash || template.version,
    background,
    objects,
  };
};

export const renderTemplate = (fabric, template, dimensions) =>
  new Promise((resolve, reject) => {
    try {
      const templateDimensions = {
        width: template.width || TEMPLATE_DEFAULT_SIZE.width,
        height: template.height || TEMPLATE_DEFAULT_SIZE.height,
      };
      const scale = fabric.util.findScaleToFit(templateDimensions, dimensions);
      const canvas = new fabric.StaticCanvas('thumb', {
        width: template.width * scale,
        height: template.height * scale,
        backgroundColor: '#FFF',
      });

      canvas.setZoom(scale);
      const json = migrateSlideContent(template.content);
      canvas.loadFromJSON(json, () => {
        canvas.asyncToDataURL(
          imageDataURL => {
            resolve(imageDataURL);
          },
          { format: 'jpeg', quality: 0.9 },
        );
      });
    } catch (error) {
      reject(error);
    }
  });
